import { OrganizationUserApiService } from "@vowkey/admin-console/common";
import { DefaultSetInitialPasswordService } from "@vowkey/angular/auth/password-management/set-initial-password/default-set-initial-password.service.implementation";
import {
  InitializeJitPasswordCredentials,
  SetInitialPasswordCredentials,
  SetInitialPasswordService,
  SetInitialPasswordUserType,
} from "@vowkey/angular/auth/password-management/set-initial-password/set-initial-password.service.abstraction";
import { InternalUserDecryptionOptionsServiceAbstraction } from "@vowkey/auth/common";
import { ApiService } from "@vowkey/common/abstractions/api.service";
import { OrganizationApiServiceAbstraction } from "@vowkey/common/admin-console/abstractions/organization/organization-api.service.abstraction";
import { MasterPasswordApiService } from "@vowkey/common/auth/abstractions/master-password-api.service.abstraction";
import { OrganizationInviteService } from "@vowkey/common/auth/organization-invite";
import { AccountCryptographicStateService } from "@vowkey/common/key-management/account-cryptography/account-cryptographic-state.service";
import { InternalMasterPasswordServiceAbstraction } from "@vowkey/common/key-management/master-password/abstractions/master-password.service.abstraction";
import { I18nService } from "@vowkey/common/platform/abstractions/i18n.service";
import { RegisterSdkService } from "@vowkey/common/platform/abstractions/sdk/register-sdk.service";
import { UserId } from "@vowkey/common/types/guid";
import { KdfConfigService, KeyService } from "@vowkey/key-management";
// eslint-disable-next-line no-restricted-imports
import { EncryptService, LegacyCompatKeyService } from "@vowkey/legacy-crypto";
import { UnlockService } from "@vowkey/unlock";
import { RouterService } from "@vowkey/web-vault/app/core";

export class WebSetInitialPasswordService
  extends DefaultSetInitialPasswordService
  implements SetInitialPasswordService
{
  constructor(
    protected apiService: ApiService,
    protected encryptService: EncryptService,
    protected i18nService: I18nService,
    protected kdfConfigService: KdfConfigService,
    protected keyService: KeyService,
    protected legacyCompatKeyService: LegacyCompatKeyService,
    protected masterPasswordApiService: MasterPasswordApiService,
    protected masterPasswordService: InternalMasterPasswordServiceAbstraction,
    protected organizationApiService: OrganizationApiServiceAbstraction,
    protected organizationUserApiService: OrganizationUserApiService,
    protected userDecryptionOptionsService: InternalUserDecryptionOptionsServiceAbstraction,
    private organizationInviteService: OrganizationInviteService,
    private routerService: RouterService,
    protected accountCryptographicStateService: AccountCryptographicStateService,
    protected registerSdkService: RegisterSdkService,
    protected unlockService: UnlockService,
  ) {
    super(
      apiService,
      encryptService,
      i18nService,
      kdfConfigService,
      keyService,
      legacyCompatKeyService,
      masterPasswordApiService,
      masterPasswordService,
      organizationApiService,
      organizationUserApiService,
      userDecryptionOptionsService,
      accountCryptographicStateService,
      registerSdkService,
      unlockService,
    );
  }

  /**
   * @deprecated use `initializePasswordJitPasswordUserV2Encryption()` instead
   */
  override async setInitialPassword(
    credentials: SetInitialPasswordCredentials,
    userType: SetInitialPasswordUserType,
    userId: UserId,
  ) {
    await super.setInitialPassword(credentials, userType, userId);

    /**
     * TODO: Investigate refactoring the following logic in https://bitwarden.atlassian.net/browse/PM-22615
     * ---
     * When a user joins an org, they can be accepted into it via three paths that all
     * converge here for state cleanup:
     *
     *  1) Direct invite (emailed link) — AcceptOrgDirectInviteComponent stashes a
     *     DirectOrganizationInvite in state. If the user has no account AND the org
     *     enforces SSO, the component redirects to /sso to accelerate JIT provisioning.
     *
     *  2) Open invite (shared link) — AcceptOrgOpenInviteComponent stashes an
     *     OpenOrganizationInvite in state. For SSO-required orgs the same redirect to
     *     /sso happens for unauthed users, entering the same JIT provisioning path.
     *
     *  3) SSO login with JIT provisioning — ExternalCallback creates an Invited
     *     OrganizationUser row scoped to the SSO org, and this finish-registration
     *     path accepts it server-side (see server - SetInitialMasterPasswordCommand.cs).
     *     Whether or not an invite (of either kind) was stashed in state up front,
     *     the JIT accept happens by SSO-org lookup, not by reading client state.
     *
     * When an invite is stashed, clear it (both direct + open state keys) and its
     * /accept-organization or /join redirect so `deepLinkGuard` doesn't replay it after JIT.
     * Without a stashed invite, the redirect is an unrelated deep link for `deepLinkGuard`
     * to replay.
     */
    if ((await this.organizationInviteService.getOrganizationInvite()) != null) {
      await this.routerService.getAndClearLoginRedirectUrl();
      await this.organizationInviteService.clearOrganizationInvite();
    }
  }

  override async initializePasswordJitPasswordUserV2Encryption(
    credentials: InitializeJitPasswordCredentials,
    userId: UserId,
  ): Promise<void> {
    await super.initializePasswordJitPasswordUserV2Encryption(credentials, userId);

    // TODO: Investigate refactoring the following logic in https://bitwarden.atlassian.net/browse/PM-22615
    // Without a stashed invite, the redirect is an unrelated deep link for `deepLinkGuard` to replay.
    if ((await this.organizationInviteService.getOrganizationInvite()) != null) {
      await this.routerService.getAndClearLoginRedirectUrl();
      await this.organizationInviteService.clearOrganizationInvite();
    }
  }
}
